import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Icon } from '@/components/ui/icon'
import { BrandLogo } from '@/components/shared/BrandLogo'
import { cn } from '@/lib/utils'
import { SHOPIFY_APP_STORE_URL } from '@/config/public-links'
import { usePublicHrefs } from './usePublicHrefs'

export function LandingNavbar() {
  const { t, i18n } = useTranslation()
  const hrefs = usePublicHrefs()
  const nextLanguage = i18n.resolvedLanguage === 'zh-TW' ? 'en' : 'zh-TW'
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  /*
   * A scroll listener fires on every frame and re-renders the whole nav to flip
   * one boolean. A sentinel at the top of the document reports the same fact
   * once per crossing, off the main thread.
   */
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      ([entry]) => setIsScrolled(!entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  const navLinks = [
    { label: t('landing:nav.guide'), href: hrefs.guide },
    { label: t('landing:nav.updates'), href: hrefs.updates },
    { label: t('landing:nav.sources'), href: hrefs.sources },
  ]

  return (
    <>
      {/* Takes no space in flow, so it cannot shift the hero. */}
      <div ref={sentinelRef} aria-hidden className="absolute inset-x-0 top-0 h-5" />

      <nav
        aria-label="Primary"
        className={cn(
          'fixed inset-x-0 top-0 z-50 border-b border-transparent transition-[background-color,border-color,backdrop-filter] duration-200',
        )}
        style={
          isScrolled
            ? {
                borderColor: 'var(--lp-rule)',
                backgroundColor: 'color-mix(in oklab, var(--lp-paper) 88%, transparent)',
                backdropFilter: 'blur(12px)',
              }
            : undefined
        }
      >
        <div className="mx-auto w-full max-w-6xl px-6 lg:px-10">
          <div className="lp-nav-inner flex h-16 items-center justify-between gap-6">
            <BrandLogo size="default" href="/" textBreakpoint="always" className="lp-brand" />

            <div className="hidden items-center gap-7 xl:flex">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="lp-nav-link whitespace-nowrap text-sm font-medium transition-colors"
                  style={{ color: 'var(--lp-body)' }}
                >
                  {link.label}
                </a>
              ))}
              <span
                className="h-4 w-px"
                style={{ backgroundColor: 'var(--lp-rule)' }}
                aria-hidden
              />
              <button
                type="button"
                className="lp-language"
                onClick={() => void i18n.changeLanguage(nextLanguage)}
                aria-label={nextLanguage === 'en' ? 'Switch to English' : '切換為繁體中文'}
              >
                {nextLanguage === 'en' ? 'EN' : '中文'}
              </button>
              <ThemeToggle />
              <a
                href={SHOPIFY_APP_STORE_URL}
                className="lp-nav-cta lp-button whitespace-nowrap text-sm font-semibold"
              >
                {t('landing:nav.install')}
              </a>
            </div>

            <div className="lp-mobile-controls flex items-center gap-1 xl:hidden">
              {/* The wrapper owns visibility: `.lp-button` sets its own display. */}
              <span className="mr-2 hidden sm:inline-flex">
                <a href={SHOPIFY_APP_STORE_URL} className="lp-nav-cta lp-button whitespace-nowrap text-sm font-semibold">
                  {t('landing:nav.install')}
                </a>
              </span>
              <button
                type="button"
                className="lp-language"
                onClick={() => void i18n.changeLanguage(nextLanguage)}
                aria-label={nextLanguage === 'en' ? 'Switch to English' : '切換為繁體中文'}
              >
                {nextLanguage === 'en' ? 'EN' : '中文'}
              </button>
              <ThemeToggle />
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((open) => !open)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md transition-colors"
                style={{ color: 'var(--lp-body)' }}
                aria-label={t(
                  isMobileMenuOpen ? 'landing:nav.close_menu' : 'landing:nav.open_menu',
                )}
                aria-expanded={isMobileMenuOpen}
                aria-controls="landing-mobile-menu"
              >
                <Icon
                  name={isMobileMenuOpen ? 'nav.close' : 'nav.menu'}
                  size="lg"
                  aria-hidden
                />
              </button>
            </div>
          </div>
        </div>

        {isMobileMenuOpen ? (
          <div
            id="landing-mobile-menu"
            className="border-t xl:hidden"
            style={{
              borderColor: 'var(--lp-rule)',
              backgroundColor: 'var(--lp-paper)',
            }}
          >
            <div className="px-6 pb-6 pt-2">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block border-b py-4 text-base font-medium"
                  style={{
                    color: 'var(--lp-ink)',
                    borderColor: 'var(--lp-rule)',
                  }}
                >
                  {link.label}
                </a>
              ))}
              <a href={SHOPIFY_APP_STORE_URL} className="lp-button mt-5">
                {t('landing:nav.install')}
              </a>
            </div>
          </div>
        ) : null}
      </nav>
    </>
  )
}
